import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Users, Lock, ArrowRight, Loader2, Inbox, Feather } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { getInviteInfo, joinHousehold } from "../lib/api";

// The household-join moment for an ALREADY-SIGNED-IN user. Picks up an invite code from a
// /join?code= deep link OR a pending code stored before sign-in (so the invite survives login,
// account creation, refresh and auth redirects). Shows a calm Accept/Cancel confirmation, or a
// clear "you already share a household" message — never moves anyone silently.
const KEY = "heldall_pending_invite";

function readPendingCode() {
  try {
    const params = new URLSearchParams(window.location.search);
    const urlCode = (params.get("code") || "").trim().toUpperCase();
    if (window.location.pathname.includes("/join") && urlCode) return urlCode;
    const stored = (localStorage.getItem(KEY) || "").trim().toUpperCase();
    return stored || null;
  } catch (_) {
    return null;
  }
}

export default function JoinInviteSheet() {
  const { user, refresh } = useAuth();
  const [code, setCode] = useState(null);
  const [info, setInfo] = useState(null); // null = loading, false = none/dismissed, object = ready
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    const c = readPendingCode();
    if (c) {
      try { localStorage.setItem(KEY, c); } catch (_) {}
      setCode(c);
    }
  }, [user]);

  useEffect(() => {
    if (!code) return;
    let alive = true;
    getInviteInfo(code)
      .then((d) => {
        if (!alive) return;
        if (!d || !d.valid || d.already_member) { clearPending(); setInfo(false); return; }
        setInfo(d);
      })
      .catch(() => { if (alive) { clearPending(); setInfo(false); } });
    return () => { alive = false; };
  }, [code]);

  const clearPending = () => {
    try { localStorage.removeItem(KEY); } catch (_) {}
    if (window.location.pathname.includes("/join")) {
      try { window.history.replaceState({}, "", "/"); } catch (_) {}
    }
  };

  const cancel = () => { clearPending(); setCode(null); setInfo(false); };

  const accept = async () => {
    setBusy(true);
    try {
      const res = await joinHousehold(code);
      clearPending();
      setCode(null);
      setInfo(false);
      await refresh();
      const who = res.inviter_name ? `${res.inviter_name}'s` : "the";
      toast.success(`You're in ${who} household. Your private items stayed private.`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || "Couldn't join the household. Please try again.");
      setBusy(false);
    }
  };

  if (!user || !code || !info) return null;

  const inviter = info.inviter_name || "Someone";
  const blocked = info.caller_in_shared_household || info.full;
  const blockMsg = info.caller_in_shared_household
    ? `You're already sharing a household. To join ${inviter}'s, leave your current one first in Account → Household.`
    : `${inviter}'s household is already full.`;

  const Line = ({ icon: Icon, children }) => (
    <li className="flex items-start gap-3">
      <span className="h-7 w-7 rounded-full bg-[var(--pine-soft)] text-[var(--pine)] flex items-center justify-center shrink-0 mt-0.5">
        <Icon size={14} strokeWidth={1.9} />
      </span>
      <span className="text-[14px] text-[var(--ink2)] leading-snug">{children}</span>
    </li>
  );

  return (
    <div
      data-testid="join-invite-moment"
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-3 sm:p-4 pb-safe bg-[rgba(28,38,34,0.34)] backdrop-blur-sm rise-in"
      onClick={cancel}
    >
      <div
        className="relative w-full max-w-md rounded-3xl bg-[var(--surface)] border border-[var(--line)] shadow-[0_24px_70px_rgba(28,38,34,0.30)] p-6 sm:p-7"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="join-invite-close"
          onClick={cancel}
          aria-label="Cancel"
          className="absolute top-4 right-4 h-8 w-8 rounded-full flex items-center justify-center text-[var(--ink4)] hover:text-[var(--ink2)] hover:bg-[var(--surface2)] transition-colors"
        >
          <X size={17} strokeWidth={2} />
        </button>

        <span className="h-11 w-11 rounded-2xl bg-[var(--pine-soft)] text-[var(--pine)] flex items-center justify-center">
          <Users size={20} strokeWidth={1.8} />
        </span>

        <h2 className="text-2xl font-semibold text-[var(--ink)] tracking-[-0.01em] mt-4 pr-8" data-testid="join-invite-headline">
          {blocked ? "You already have a household" : `${inviter} invited you to share a household`}
        </h2>

        {blocked ? (
          <>
            <p className="text-[15px] text-[var(--ink2)] mt-2.5 leading-relaxed" data-testid="join-invite-blocked">
              {blockMsg}
            </p>
            <button
              data-testid="join-invite-ok"
              onClick={cancel}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[var(--surface2)] hover:bg-[var(--line2)] text-[var(--ink)] font-semibold px-6 py-3 text-sm transition-colors"
            >
              Got it
            </button>
          </>
        ) : (
          <>
            <p className="text-[15px] text-[var(--ink2)] mt-2.5 leading-relaxed">
              Joining puts you both in the same household. Nothing of yours is shared automatically.
            </p>
            <ul className="mt-5 space-y-3">
              <Line icon={Lock}>Your private items stay private — only you can see them.</Line>
              <Line icon={Inbox}>Shared things appear only when one of you chooses to share or hand something over.</Line>
              <Line icon={Feather}>You can still use Heldall just for yourself, exactly as now.</Line>
            </ul>
            <div className="mt-7 flex flex-col sm:flex-row gap-2.5">
              <button
                data-testid="join-invite-accept"
                onClick={accept}
                disabled={busy}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[var(--pine)] hover:bg-[var(--pine-dark)] disabled:opacity-50 text-[var(--on-pine)] font-semibold px-6 py-3.5 text-sm shadow-[0_10px_28px_rgba(44,90,77,0.28)] transition-all duration-200"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} strokeWidth={2} />}
                Join {inviter}'s household
              </button>
              <button
                data-testid="join-invite-cancel"
                onClick={cancel}
                disabled={busy}
                className="rounded-full px-6 py-3.5 text-sm font-semibold text-[var(--ink3)] hover:text-[var(--ink)] hover:bg-[var(--surface2)] transition-colors disabled:opacity-50"
              >
                Not now
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
